import React, { useContext, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Box, Typography, useMediaQuery, Rating, Stack } from '@mui/material';
import { useStyle } from '../../context/styleContext/styleContext';
import { ShoppingCartContext } from '../../context/shoppingCartContext/shoppingCartContext';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import shoppingCartIcon from '../../images/shopping_cart.svg';

export default function HomePageProductCard({ productData }) {
  const [data, setData] = useState(null);
  const { addToCart } = useContext(ShoppingCartContext);
  const { style } = useStyle();
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    if (productData) {
      setData(productData);
    }
  }, [productData]);

  const addProductToCart = (e) => {
    e.preventDefault();
    addToCart(productData.SKU);
  };

  const getImageWidth = () => {
    if (!productData?.productURI) return '72%';
    return productData.productURI === 'd1-delay' ? '88%' : '68%';
  };

  const formatPrice = (price) => Number(price).toFixed(2);

  if (!data) return null;

  return (
    <>
      {!isMobile && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 520,
            display: 'flex',
            flexDirection: 'column',
            padding:'10px',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: '1.45rem',
              mb: 0.75,
              mt: 0.5,
              fontWeight: 'bold',
              minHeight: 56,
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <Box
              component={RouterLink}
              to={`/products/${productData.productURI}`}
              sx={{
                color: style.color4,
                textDecoration: 'none',
                '&:hover': { color: style.color3 },
              }}
            >
              {data.name}
            </Box>
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 210,
              maxHeight: 210,
              height: 210,
              borderRadius: '8px',
              mb: 1,
            }}
          >
            <Box
              component={RouterLink}
              to={`/products/${productData.productURI}`}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src={productData.images[0]?.URI}
                alt={productData.images[0]?.altText}
                sx={{
                  width: getImageWidth(),
                  maxWidth: '100%',
                  maxHeight: 200,
                  borderRadius: 1,
                  objectFit: 'contain',
                  backgroundColor: 'rgba(0,0,0,.3)',
                  p: '5px',
                  display: 'block',
                }}
              />
            </Box>
          </Box>

          <Stack spacing={0.75} sx={{ flexGrow: 1 }}>
            <Box
              component={RouterLink}
              to={`/reviews/${productData.productURI}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                textDecoration: 'none',
                color: 'inherit',
                width: 'fit-content',
                '&:hover': {
                  opacity: 0.9,
                },
              }}
            >
              <Rating
                value={Number(productData.starRating || 0)}
                precision={0.5}
                readOnly
                sx={{ml:1}}
              />
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize:'1.2rem'}}>
                {Number(productData.starRating || 0) > 0 ? productData.starRating : ''}
              </Typography>
              <Typography variant="body2" sx={{ color: style.color3 }}>
                ({productData.ratingCount || 0} review{Number(productData.ratingCount) === 1 ? '' : 's'})
              </Typography>
            </Box>

            <Typography
              sx={{
                minHeight: 48,
                maxHeight: 48,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
              }}
              variant="body1"
            >
              {productData.cardSummary}
            </Typography>

            <Typography sx={{ fontSize: '1rem' }} variant="body1">
              <strong>VST3 Plugin</strong>
            </Typography>

            {Number(productData.price) > 0 ? (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    color: style.color4,
                    fontSize: '1.3rem',
                    textAlign: 'left',
                    lineHeight: 1.1,
                  }}
                >
                  <strike style={{ fontSize: '1.2rem', color: style.color3 }}>
                    ${formatPrice(productData.msrp)}
                  </strike>
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: style.color4,
                    fontSize: '1.45rem',
                    textAlign: 'left',
                    lineHeight: 1.1,
                  }}
                >
                  <strong>${formatPrice(productData.price)}</strong>
                </Typography>
              </>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  color: style.color4,
                  fontSize: '1.35rem',
                  textAlign: 'left',
                  lineHeight: 1.2,
                }}
              >
                <strong style={{ color: 'rgb(0,200,50)' }}>FREE</strong>{' '}
                <span style={{ fontSize: '0.95rem' }}>(limited time)</span>
              </Typography>
            )}

            <Box sx={{ mt: 'auto', pt: 1 }}>
              {Number(productData.price) > 0 ? (
                <Stack spacing={1.25}>
                  <Button
                    onClick={addProductToCart}
                    sx={{
                      backgroundColor: style.color1,
                      color: style.color4,
                      border: `1px solid ${style.color2}`,
                      width: '100%',
                      filter: 'brightness(1.05)',
                      boxShadow: 10,
                      fontWeight: 'bold',
                      fontSize: '.85rem',
                      '&:hover': { filter: 'brightness(1.2)' },
                    }}
                  >
                    <img
                      src={shoppingCartIcon}
                      alt="Shopping cart icon"
                      style={{ maxWidth: '20px', marginRight: '5px' }}
                    />
                    Add to Cart
                  </Button>

                  <Button
                    component={RouterLink}
                    to={`/products/${productData.productURI}`}
                    sx={{
                      backgroundColor: style.color1,
                      color: style.color4,
                      border: `1px solid ${style.color2}`,
                      width: '100%',
                      filter: 'brightness(1.05)',
                      boxShadow: 10,
                      fontWeight: 'bold',
                      fontSize: '.85rem',
                      '&:hover': { filter: 'brightness(1.2)' },
                    }}
                  >
                    <KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize: '1.5rem', marginRight: '5px' }} />
                    Learn More
                  </Button>
                </Stack>
              ) : (
                <Button
                  component={RouterLink}
                  to={`/products/${productData.productURI}`}
                  sx={{
                    width: '100%',
                    fontWeight: 'bold',
                    color: style.color4,
                    backgroundColor: style.color2,
                    filter: 'brightness(1.05)',
                    transition: 'filter 0.3s ease',
                    '&:hover': { filter: 'brightness(1.2)' },
                  }}
                >
                  Learn More
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      {isMobile && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 300,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 150,
                minWidth: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
              }}
            >
              <Box
                component={RouterLink}
                to={`/products/${productData.productURI}`}
                sx={{
                  width: '100%',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={productData.images[0]?.URI}
                  alt={productData.images[0]?.altText}
                  sx={{
                    width: productData.productURI === 'd1-delay' ? '100%' : '82%',
                    maxHeight: 150,
                    objectFit: 'contain',
                    backgroundColor: 'rgba(0,0,0,.3)',
                    p: '5px',
                    borderRadius: 1,
                    display: 'block',
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', flexGrow: 1, pl: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '1.35rem',
                  mb: 0.5,
                  mt: 0.5,
                  fontWeight: 'bold',
                  lineHeight: 1.15,
                }}
              >
                <Box
                  component={RouterLink}
                  to={`/products/${productData.productURI}`}
                  sx={{
                    color: style.color4,
                    textDecoration: 'none',
                    '&:hover': { color: style.color3 },
                  }}
                >
                  {data.name}
                </Box>
              </Typography>

              <Box
                component={RouterLink}
                to={`/reviews/${productData.productURI}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexWrap: 'wrap',
                  textDecoration: 'none',
                  color: 'inherit',
                  width: 'fit-content',
                  mb: 0.5,
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              >
                <Rating
                  value={Number(productData.starRating || 0)}
                  precision={0.5}
                  readOnly
                  size="small"
                  fontSize='3rem'
                />
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize:'1.2rem'}}>
                  {Number(productData.starRating || 0) > 0 ? productData.starRating : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: style.color3 }}>
                  ({productData.ratingCount || 0} review{Number(productData.ratingCount) === 1 ? '' : 's'})
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: '.78rem',
                  mt: 0.25,
                  textAlign: 'left',
                  minHeight: 38,
                  maxHeight: 38,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
                variant="body1"
              >
                {productData.cardSummary}
              </Typography>

              <Typography sx={{ mt: 1, fontSize: '0.95rem', textAlign: 'left' }} variant="body1">
                <strong>VST3 Plugin</strong>
              </Typography>

              {Number(productData.price) > 0 ? (
                <Typography
                  variant="h6"
                  sx={{
                    mt: 0.5,
                    color: style.color4,
                    fontSize: '1.2rem',
                    textAlign: 'left',
                    lineHeight: 1.2,
                  }}
                >
                  <strike style={{ fontSize: '1rem', color: style.color3 }}>
                    ${formatPrice(productData.msrp)}
                  </strike>
                  <strong style={{ fontSize: '1.25rem', marginLeft: '10px' }}>
                    ${formatPrice(productData.price)}
                  </strong>
                </Typography>
              ) : (
                <Typography
                  variant="h6"
                  sx={{
                    mt: 0.5,
                    color: style.color4,
                    fontSize: '1.2rem',
                    textAlign: 'left',
                  }}
                >
                  <strong style={{ color: 'rgb(0,200,50)' }}>FREE</strong>{' '}
                  <span style={{ fontSize: '0.9rem' }}>(limited time)</span>
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 'auto', pt: 1 }}>
            {Number(productData.price) > 0 ? (
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={addProductToCart}
                  sx={{
                    backgroundColor: style.color1,
                    color: style.color4,
                    border: `1px solid ${style.color2}`,
                    flex: 1,
                    filter: 'brightness(1.05)',
                    boxShadow: 10,
                    fontWeight: 'bold',
                    fontSize: '.8rem',
                    '&:hover': { filter: 'brightness(1.2)' },
                  }}
                >
                  <img
                    src={shoppingCartIcon}
                    alt="Shopping cart icon"
                    style={{ maxWidth: '20px', marginRight: '5px' }}
                  />
                  Add to Cart
                </Button>

                <Button
                  component={RouterLink}
                  to={`/products/${productData.productURI}`}
                  sx={{
                    backgroundColor: style.color1,
                    color: style.color4,
                    border: `1px solid ${style.color2}`,
                    flex: 1,
                    filter: 'brightness(1.05)',
                    boxShadow: 10,
                    fontWeight: 'bold',
                    fontSize: '.8rem',
                    '&:hover': { filter: 'brightness(1.2)' },
                  }}
                >
                  <KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize: '1.4rem', marginRight: '4px' }} />
                  Learn More
                </Button>
              </Stack>
            ) : (
              <Button
                component={RouterLink}
                to={`/products/${productData.productURI}`}
                sx={{
                  width: '100%',
                  fontWeight: 'bold',
                  color: style.color4,
                  backgroundColor: style.color2,
                  filter: 'brightness(1.05)',
                  transition: 'filter 0.3s ease',
                  '&:hover': { filter: 'brightness(1.2)' },
                }}
              >
                Learn More
              </Button>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}